import { FC, useEffect, useMemo, useRef, useState } from "react"
import Autocomplete from "../form/autocomplete"
import { useSearchLoanOfficerQuery } from "../../../redux/queries"
import { LoanOfficer, LoSearchParams } from "../../../types"
import { checkIsAdmin } from "../../utils"

type SearchMode = "name" | "nmls"

interface Props {
  searchParams?: LoSearchParams
  selected?: string
  label?: string
  direction?: "vertical" | "horizontal"
  onlyAdmins?: boolean
  required?: boolean
  excludeIds?: string[]
  showBrandName?: boolean
  showSearchToggle?: boolean
  onChange: (val: string) => void
  onSelectLo?: (lo: LoanOfficer) => void
}

const convertLoToAutocompleteOption = (
  lo: LoanOfficer,
  showBrandName = false
) => ({
  id: lo.uid,
  text:
    showBrandName && lo.brand_name ? `${lo.name} (${lo.brand_name})` : lo.name
})

const LoanOfficerAutocomplete: FC<Props> = ({
  searchParams = {},
  label = "Loan officer:",
  selected,
  direction = "horizontal",
  onlyAdmins,
  required = false,
  excludeIds = [],
  showBrandName = false,
  showSearchToggle = false,
  onChange,
  onSelectLo
}) => {
  const [query, setQuery] = useState<string>("")
  const [debouncedQuery, setDebouncedQuery] = useState<string>("")
  const [searchMode, setSearchMode] = useState<SearchMode>("name")
  // Store LO data in a map so we can find it when selected (even after search results change)
  const loMapRef = useRef<Map<string, LoanOfficer>>(new Map())

  // Debounce query to reduce API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(query)
    }, 300) // 300ms debounce delay

    return () => clearTimeout(timeoutId)
  }, [query])

  const searchQueryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      limit: "10",
      ...searchParams
    }
    if (searchMode === "name") {
      params.fullname = debouncedQuery
    } else {
      params.nmls = debouncedQuery
    }
    return params
  }, [searchMode, debouncedQuery, searchParams])

  const { data, isFetching } = useSearchLoanOfficerQuery(searchQueryParams)

  // Update the LO map whenever we get new search results
  useEffect(() => {
    if (data) {
      data.forEach(lo => {
        loMapRef.current.set(lo.uid, lo)
      })
    }
  }, [data])

  const filteredData = useMemo(() => {
    let filtered = data ?? []

    if (onlyAdmins) {
      filtered = filtered.filter(lo => checkIsAdmin(lo.roles ?? []))
    }

    if (excludeIds.length > 0) {
      filtered = filtered.filter(lo => !excludeIds.includes(lo.uid))
    }

    return filtered
  }, [data, onlyAdmins, excludeIds])

  const options = useMemo(
    () =>
      filteredData.map(lo => convertLoToAutocompleteOption(lo, showBrandName)),
    [filteredData, showBrandName]
  )

  const selectedOption = options.find(opt => opt.id === selected)

  const { data: selectedLo, isFetching: isFetchingSelectedLo } =
    useSearchLoanOfficerQuery(
      {
        loid: selected ?? ""
      },
      { skip: !selected || !!selectedOption }
    )

  // Also add selected LO to the map
  useEffect(() => {
    if (selectedLo?.[0]) {
      loMapRef.current.set(selectedLo[0].uid, selectedLo[0])
    }
  }, [selectedLo])

  const handleChange = (option: { id: string; text: string }) => {
    onChange(option.id)
    if (onSelectLo) {
      // Look up in the map first, then fall back to current data
      const lo =
        loMapRef.current.get(option.id) ??
        filteredData.find(l => l.uid === option.id) ??
        selectedLo?.[0]
      if (lo) {
        onSelectLo(lo)
      }
    }
  }

  const handleSearchModeChange = (mode: SearchMode) => {
    setSearchMode(mode)
    setQuery("")
    setDebouncedQuery("")
  }

  return (
    <div className="max-w-md">
      {showSearchToggle && (
        <div className="flex gap-1 mb-2">
          <button
            type="button"
            onClick={() => handleSearchModeChange("name")}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              searchMode === "name"
                ? "bg-primary-100 text-primary-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Name
          </button>
          <button
            type="button"
            onClick={() => handleSearchModeChange("nmls")}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              searchMode === "nmls"
                ? "bg-primary-100 text-primary-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            NMLS
          </button>
        </div>
      )}
      <Autocomplete
        label={label}
        name="loan_officer"
        placeholder={
          searchMode === "name" ? "Search by name..." : "Search by NMLS..."
        }
        value={
          selectedOption ??
          (selectedLo?.[0]
            ? convertLoToAutocompleteOption(selectedLo?.[0], showBrandName)
            : undefined)
        }
        query={query}
        options={options}
        direction={direction}
        required={required}
        onChange={handleChange}
        onChangeQuery={setQuery}
        isLoading={isFetching || isFetchingSelectedLo}
      />
    </div>
  )
}

export default LoanOfficerAutocomplete
